import { Autocomplete, TextField, Typography } from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import { useMenuItems } from '@/api/hooks'
import type { MenuItem } from '@/api/types'

/**
 * The little a picker, a portion row and a chip actually need of a dish.
 *
 * <p>Narrower than a catalogue record on purpose: a package being edited knows
 * its choices only by what the package itself stored - a code, a name - and
 * should not have to fetch the whole dish back to draw them.</p>
 */
export type Dish = Pick<MenuItem, 'id' | 'itemCode' | 'name'> &
  Partial<Pick<MenuItem, 'categoryId' | 'categoryName' | 'servingUnit'>>

/**
 * Every dish the pickers on a screen have handed back, kept by id.
 *
 * <p>The catalogue is searched a page at a time, so the results behind one
 * choice are gone by the time the next search runs. Without somewhere to
 * remember them, a dish chosen under "payasam" would vanish from the item as
 * soon as somebody typed "vadai".</p>
 */
export function useDishMemory(initial?: Dish[]) {
  const [known, setKnown] = useState<Map<string, Dish>>(
    () => new Map((initial ?? []).map((dish) => [dish.id, dish])),
  )
  const remember = useCallback((dishes: Dish[]) => {
    setKnown((current) => {
      const missing = dishes.filter((dish) => !current.has(dish.id))
      if (missing.length === 0) return current
      const next = new Map(current)
      missing.forEach((dish) => next.set(dish.id, dish))
      return next
    })
  }, [])
  return { known, remember }
}

/**
 * Picking the dishes a package item offers.
 *
 * <p>Searched on the server by code or name, so typing "924" and typing "payasam"
 * both find the same dish, and a catalogue of thousands is not first pulled into
 * the browser to be filtered there. The server answers in item-code order, which
 * decides which page of a wide search comes back; the few shown here are then
 * listed by name, the way somebody choosing a dish looks for one.</p>
 *
 * <p>Only categories a package may offer from appear - finished dishes and
 * bought-in lines, not the kitchen's semi-finished goods, which are a step in
 * production and were never anybody's choice on a menu card.</p>
 *
 * <p>Dishes already chosen are always kept among the options. Without that a
 * search for something else would drop them from the list and, with them, the
 * chips showing what the item already offers.</p>
 */
export function DishPicker({
  label,
  chosen,
  onChange,
}: {
  label: string
  chosen: Dish[]
  onChange: (dishes: Dish[]) => void
}) {
  const [term, setTerm] = useState('')
  const { data, isFetching } = useMenuItems({
    q: term.trim() || undefined,
    offeredInPackages: true,
    size: 50,
  })

  const options = useMemo(() => {
    const byId = new Map<string, Dish>()
    chosen.forEach((dish) => byId.set(dish.id, dish))
    ;(data?.content ?? []).forEach((dish) => byId.set(dish.id, dish))
    return [...byId.values()].sort((left, right) => left.name.localeCompare(right.name))
  }, [data, chosen])

  return (
    <Autocomplete
      multiple
      size="small"
      options={options}
      value={chosen}
      loading={isFetching}
      // The server has already matched the term against both code and name;
      // filtering again here would only narrow what it deliberately returned.
      filterOptions={(all) => all}
      // What is already chosen shows as a chip. Leaving it in the list as well
      // means a search for something else answers with what you picked before.
      filterSelectedOptions
      inputValue={term}
      onInputChange={(_event, value, reason) => {
        if (reason !== 'reset') setTerm(value)
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => `${option.itemCode} — ${option.name}`}
      onChange={(_event, value) => onChange(value)}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Typography variant="body2">
            {option.name}
            <Typography component="span" variant="caption" color="text.secondary">
              {' '}
              · {option.itemCode}
              {option.categoryName ? ` · ${option.categoryName}` : ''}
            </Typography>
          </Typography>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Search by code or name"
          helperText={
            data && data.totalElements > data.content.length
              ? `Showing ${data.content.length} of ${data.totalElements} — keep typing to narrow it`
              : undefined
          }
        />
      )}
    />
  )
}
